import type { Value } from 'platejs'
import type { TPlateEditor } from 'platejs/react'

import { withoutSavingHistory } from '~publish/legacy/editor/BufferEditor/history'

/**
 * Removes one or more properties from every node in the editor through Slate's
 * immutable transform API.
 *
 * We can't `delete node[property]` directly: Slate deep-freezes its node tree,
 * so deleting a non-configurable property on a frozen node throws
 * `TypeError: Cannot delete property '…' of #<Object>`. `unsetNodes` applies the
 * change as a normalized transform that builds new nodes instead of mutating the
 * frozen ones.
 *
 * This is a background cleanup (fired from a detached `setTimeout`), so we wrap
 * it in `withoutSavingHistory`: it must not add an undo entry, otherwise a
 * Ctrl+Z right after generation would restore the flags and replay the flash
 * animation. Normalization stays on so the now-identically-marked adjacent
 * leaves still merge.
 */
const unsetPropertyInEditor = <TValue extends Value>(
  editor: TPlateEditor<TValue>,
  property: string | readonly string[],
): void => {
  const properties = typeof property === 'string' ? [property] : property

  const start = editor.api.start([])
  const end = editor.api.end([])
  if (!start || !end) return

  withoutSavingHistory(editor, () => {
    editor.tf.unsetNodes([...properties], {
      // Span the whole document as a range (a path `at` would force the match to
      // that single node), and match any node carrying one of the properties at
      // any depth.
      at: { anchor: start, focus: end },
      match: (node) => properties.some((key) => key in node),
      mode: 'all',
    })
  })
}

export default unsetPropertyInEditor
